"use client";

import AcSpecFields from "@/components/listing/AcSpecFields";
import ChairMaterialFields from "@/components/listing/ChairMaterialFields";
import ChairSpecFields from "@/components/listing/ChairSpecFields";
import DiningTableChairSpecFields from "@/components/listing/DiningTableChairSpecFields";
import FanSpecFields from "@/components/listing/FanSpecFields";
import FridgeSpecFields from "@/components/listing/FridgeSpecFields";
import FurnitureSurfaceSpecFields from "@/components/listing/FurnitureSurfaceSpecFields";
import GasCookerSpecFields from "@/components/listing/GasCookerSpecFields";
import KitchenCabinetSpecFields from "@/components/listing/KitchenCabinetSpecFields";
import MicrowaveSpecFields from "@/components/listing/MicrowaveSpecFields";
import OfficeTableChairsBundleSpecFields from "@/components/listing/OfficeTableChairsBundleSpecFields";
import PlayStationSpecFields from "@/components/listing/PlayStationSpecFields";
import SewingMachineSpecFields from "@/components/listing/SewingMachineSpecFields";
import TableChairBundleSpecFields from "@/components/listing/TableChairBundleSpecFields";
import WashingMachineSpecFields from "@/components/listing/WashingMachineSpecFields";
import FieldError from "@/components/ui/FieldError";
import type { AcSpecFormState } from "@/lib/listing/acListingSpec";
import {
  getSeatingSpecSectionLabel,
  getSeatingSpecUiVariant,
  isChairMaterialOnlySubcategory,
  type ChairSpecFormState,
} from "@/lib/listing/chairListingSpec";
import type { DiningTableChairSpecFormState } from "@/lib/listing/diningTableChairListingSpec";
import type { FanSpecFormState } from "@/lib/listing/fanListingSpec";
import type { FridgeSpecFormState } from "@/lib/listing/fridgeListingSpec";
import {
  getFurnitureSurfaceSpecSectionLabel,
  isFurnitureSurfaceSubcategory,
  type FurnitureSurfaceSpecFormState,
} from "@/lib/listing/furnitureSurfaceListingSpec";
import type { FurnitureTableBundleSpecFormState } from "@/lib/listing/furnitureTableBundleListingSpec";
import type { GasCookerSpecFormState } from "@/lib/listing/gasCookerListingSpec";
import type { KitchenCabinetSpecFormState } from "@/lib/listing/kitchenCabinetListingSpec";
import {
  readFamilySpec,
  withFamilySpec,
  type ListingFamilySpecs,
} from "@/lib/listing/listingFamilyRuntime";
import { getListingFamilyById } from "@/lib/listing/listingFamilyRegistry";
import { getFamilyRuntime } from "@/lib/listing/listingFamilyRuntime";
import type { MicrowaveSpecFormState } from "@/lib/listing/microwaveListingSpec";
import type { PlayStationSpecFormState } from "@/lib/listing/playStationListingSpec";
import type { SewingMachineSpecFormState } from "@/lib/listing/sewingMachineListingSpec";
import type { WashingMachineSpecFormState } from "@/lib/listing/washingMachineListingSpec";

type ListingFamilySpecFieldsHostProps = {
  subcategoryId: string;
  familySpecs: ListingFamilySpecs;
  onChange: (next: ListingFamilySpecs) => void;
  error?: string;
  className?: string;
  /** Demand capture: all fields optional — no required marker */
  optionalMode?: boolean;
};

/**
 * One Step 2 renderer for registry families. The only shell importer of family
 * SpecFields — add new families here (+ runtime), not in sell/edit/bulk kind branches.
 */
export default function ListingFamilySpecFieldsHost({
  subcategoryId,
  familySpecs,
  onChange,
  error,
  className = "",
  optionalMode = false,
}: ListingFamilySpecFieldsHostProps) {
  const runtime = getFamilyRuntime(subcategoryId);
  if (!runtime) return null;

  const family = getListingFamilyById(runtime.familyId);
  const value = readFamilySpec(familySpecs, runtime.familyId);
  const onSliceChange = (next: unknown) => {
    onChange(withFamilySpec(familySpecs, runtime.familyId, next));
  };

  const seatingLabel =
    runtime.familyId === "chair" || runtime.familyId === "sofa"
      ? getSeatingSpecSectionLabel(subcategoryId)
      : null;
  const surfaceLabel =
    runtime.familyId === "furniture_surface" &&
    isFurnitureSurfaceSubcategory(subcategoryId)
      ? getFurnitureSurfaceSpecSectionLabel(subcategoryId)
      : null;
  const sectionLabel =
    seatingLabel ?? surfaceLabel ?? family?.sectionLabel ?? "Details";
  const showRequiredMarker = optionalMode
    ? false
    : runtime.familyId === "furniture_surface"
      ? subcategoryId !== "office-table"
      : true;

  return (
    <div className={className ?? "md:col-span-2"}>
      <label className="block mb-2 text-sm font-medium text-foreground">
        {sectionLabel}{" "}
        {showRequiredMarker ? (
          <span className="text-red-500">*</span>
        ) : optionalMode ? (
          <span className="text-text-light font-normal">(optional)</span>
        ) : null}
      </label>
      {runtime.familyId === "sewing_machine" ? (
        <SewingMachineSpecFields
          value={value as SewingMachineSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "play_station" ? (
        <PlayStationSpecFields
          value={value as PlayStationSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "fan" ? (
        <FanSpecFields
          value={value as FanSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "fridge" ? (
        <FridgeSpecFields
          value={value as FridgeSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "gas_cooker" ? (
        <GasCookerSpecFields
          value={value as GasCookerSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "microwave" ? (
        <MicrowaveSpecFields
          value={value as MicrowaveSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "washing_machine" ? (
        <WashingMachineSpecFields
          value={value as WashingMachineSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "ac" ? (
        <AcSpecFields
          value={value as AcSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "kitchen_cabinet" ? (
        <KitchenCabinetSpecFields
          value={value as KitchenCabinetSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "chair" || runtime.familyId === "sofa" ? (
        isChairMaterialOnlySubcategory(subcategoryId) ? (
          <ChairMaterialFields
            value={value as ChairSpecFormState}
            onChange={onSliceChange}
          />
        ) : (
          <ChairSpecFields
            value={value as ChairSpecFormState}
            onChange={onSliceChange}
            showSofaShape={getSeatingSpecUiVariant(subcategoryId) === "sofa"}
          />
        )
      ) : runtime.familyId === "dining_table_chair" ? (
        <DiningTableChairSpecFields
          value={value as DiningTableChairSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "table_chair" ? (
        <TableChairBundleSpecFields
          value={value as FurnitureTableBundleSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "office_table_chairs" ? (
        <OfficeTableChairsBundleSpecFields
          value={value as FurnitureTableBundleSpecFormState}
          onChange={onSliceChange}
        />
      ) : runtime.familyId === "furniture_surface" &&
        isFurnitureSurfaceSubcategory(subcategoryId) ? (
        <FurnitureSurfaceSpecFields
          subcategoryId={subcategoryId}
          value={value as FurnitureSurfaceSpecFormState}
          onChange={onSliceChange}
        />
      ) : null}
      <FieldError message={error} />
    </div>
  );
}
